// Connectors for Project Online XML, Monday.com GraphQL, and Smartsheet REST.
// All functions run fully in the browser. The CORS-affected SaaS connectors
// (Monday, Smartsheet, Jira, Asana, ClickUp) route through the import proxy;
// Trello is CORS-friendly and stays on direct fetch.
// Caller holds onto the API token in component state only (never persisted).
import { IPublicClientApplication } from '@azure/msal-browser';
import { getToken } from '../auth/authService';
import { proxyFetch } from './importProxy';

// ── Microsoft Project XML parser ──────────────────────────────────────

// dagrun_pm_dependency.dagrun_pm_dependencytype (verified via EntityDefinitions):
//   FS=100000000, SS=100000001, FF=100000002, SF=100000003.
// MS Project PredecessorLink Type is 0=FS, 1=SS, 2=FF, 3=SF — a direct offset.
const MPP_DEPENDENCY_TYPE = [100000000, 100000001, 100000002, 100000003];

export interface ImportPredecessor { uid: string; type: number }

// Scheduling constraint detected on a source task. typeValue/typeLabel match
// the dagrun_pm_constrainttype option set; date is ISO (may carry a time).
export interface ImportConstraint { typeValue: number; typeLabel: string; date: string | null }

export interface ImportTaskMeta {
  uid: string;
  collectionIndex: number;          // index into ParsedProjectXml.collections
  predecessors: ImportPredecessor[];
  extraFields?: Record<string, unknown>;  // dagrun_pm_* values set directly (e.g. baselines)
  constraint?: ImportConstraint;    // present only for non-default constraints
}

export interface ParsedProjectXml {
  headers: string[];
  rows: unknown[][];                // OutlineLevel 2+ work-item rows, aligned to headers
  collections: string[];            // OutlineLevel 1 task names (phases), in document order
  taskMeta: ImportTaskMeta[];       // aligned to rows — collection + predecessor metadata
  dependencyCount: number;          // total predecessor links across the work items
  constraintCount: number;          // work items carrying a non-default scheduling constraint
}

// MS Project ConstraintType → dagrun_pm_constrainttype option values.
// MS Project: 0=ASAP, 1=ALAP, 2=Must Start On, 3=Must Finish On,
// 4=Start No Earlier Than, 5=Start No Later Than, 6=Finish No Earlier Than,
// 7=Finish No Later Than. The four types the schema preserves map below;
// ASAP/ALAP/SNLT/FNET have no target option and import as default (ASAP).
const MPP_CONSTRAINT_TYPE: Record<string, { value: number; label: string }> = {
  '2': { value: 100000001, label: 'Must Start On' },
  '3': { value: 100000002, label: 'Must Finish On' },
  '4': { value: 100000003, label: 'Start No Earlier Than' },
  '7': { value: 100000004, label: 'Finish No Later Than' },
};

// MS Project XML stores Work and Duration as ISO-8601 durations
// (e.g. "PT8H0M0S" = 8 hours; "PT480M0S" = 8 hours = 480 minutes).
// The Duration is typically in minutes for the scheduling engine. For Work
// we just extract hours+minutes directly.
function msDurationToHours(s: string | null | undefined): number | null {
  if (!s) return null;
  // ISO-8601 duration: P[nW] | P[nY][nM][nD]T[nH][nM][nS]. MS Project uses
  // the PT...H...M...S form.
  const m = s.match(/PT(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?/);
  if (!m) return null;
  const h = parseFloat(m[1] ?? '0') || 0;
  const min = parseFloat(m[2] ?? '0') || 0;
  const sec = parseFloat(m[3] ?? '0') || 0;
  return Math.round((h + min / 60 + sec / 3600) * 100) / 100;
}

function textOf(parent: Element, tag: string): string | null {
  // MS Project XML keeps a default namespace — querySelector handles that
  // because tags are addressable by local name via *|tag in some browsers,
  // but the cleaner approach is to iterate children. Namespace-scope-safe
  // lookup: walk direct children checking local name.
  for (let i = 0; i < parent.children.length; i++) {
    const c = parent.children[i];
    if (c.localName === tag || c.tagName === tag) return c.textContent;
  }
  return null;
}

function findChildren(parent: Element, tag: string): Element[] {
  const out: Element[] = [];
  for (let i = 0; i < parent.children.length; i++) {
    const c = parent.children[i];
    if (c.localName === tag || c.tagName === tag) out.push(c);
  }
  return out;
}

export function parseMsProjectXml(xml: string): ParsedProjectXml {
  const doc = new DOMParser().parseFromString(xml, 'application/xml');
  const err = doc.querySelector('parsererror');
  if (err) throw new Error('Project XML parse error — the file does not appear to be a valid MS Project XML export.');

  const root = doc.documentElement;
  if (root.localName !== 'Project') {
    throw new Error('This does not look like a Microsoft Project XML file (root element is not <Project>).');
  }

  const tasksRoot = findChildren(root, 'Tasks')[0];
  if (!tasksRoot) throw new Error('No <Tasks> element found in the Project XML.');

  const tasks = findChildren(tasksRoot, 'Task');

  const headers = [
    'Task Name', 'Start', 'Finish', '% Complete',
    'Estimated Hours', 'Resources', 'WBS',
  ];

  // Also collect a resource lookup in case Task elements only carry
  // ResourceID references — extract ResourceNames where present.
  const resourceLookup = new Map<string, string>();
  const resRoot = findChildren(root, 'Resources')[0];
  if (resRoot) {
    findChildren(resRoot, 'Resource').forEach(r => {
      const uid = textOf(r, 'UID') ?? '';
      const name = textOf(r, 'Name') ?? '';
      if (uid && name) resourceLookup.set(uid, name);
    });
  }
  const assignmentsRoot = findChildren(root, 'Assignments')[0];
  const taskAssignments = new Map<string, string[]>();
  if (assignmentsRoot) {
    findChildren(assignmentsRoot, 'Assignment').forEach(a => {
      const taskUid = textOf(a, 'TaskUID');
      const resUid  = textOf(a, 'ResourceUID');
      if (!taskUid || !resUid) return;
      const name = resourceLookup.get(resUid);
      if (!name) return;
      const list = taskAssignments.get(taskUid) ?? [];
      list.push(name);
      taskAssignments.set(taskUid, list);
    });
  }

  const rows: unknown[][] = [];
  const collections: string[] = [];
  const taskMeta: ImportTaskMeta[] = [];
  let currentCollectionIndex = -1;   // nearest preceding OutlineLevel 1
  let dependencyCount = 0;
  let constraintCount = 0;

  tasks.forEach(t => {
    const uid  = textOf(t, 'UID') ?? '';
    const name = textOf(t, 'Name');
    // Skip null / project-root placeholder rows.
    if (!name) return;

    const outline = textOf(t, 'OutlineLevel');
    // Outline level 0 or "ProjectSummaryTask" rows are the project-level
    // wrapper — never imported.
    if (outline === '0' || textOf(t, 'ProjectSummaryTaskID') === '1') return;

    const level = Number(outline) || 1;

    // OutlineLevel 1 → a phase/summary that becomes a collection.
    if (level === 1) {
      collections.push(name);
      currentCollectionIndex = collections.length - 1;
      return;
    }

    // OutlineLevel 2+ → a work item that becomes a task.
    const start  = textOf(t, 'Start');
    const finish = textOf(t, 'Finish');
    const pct    = textOf(t, 'PercentComplete');
    const work   = textOf(t, 'Work');               // ISO duration
    const wbs    = textOf(t, 'WBS');

    // MS Project's XML typically emits ResourceNames on the Task — prefer
    // that, fall back to the assignment-based lookup.
    let resources = textOf(t, 'ResourceNames') ?? '';
    if (!resources && taskAssignments.has(uid)) {
      resources = (taskAssignments.get(uid) ?? []).join(', ');
    }

    // Predecessor links → dependency metadata (resolved to task GUIDs later).
    const predecessors: ImportPredecessor[] = [];
    findChildren(t, 'PredecessorLink').forEach(link => {
      const predUid = textOf(link, 'PredecessorUID');
      if (!predUid) return;
      const typeIdx = Math.max(0, Math.min(3, Number(textOf(link, 'Type') ?? '0') || 0));
      predecessors.push({ uid: predUid, type: MPP_DEPENDENCY_TYPE[typeIdx] });
    });
    dependencyCount += predecessors.length;

    // Scheduling constraint — previously parsed context discarded it; now it
    // rides through extraFields onto the task record and through .constraint
    // into the preview banner / review list.
    const constraintDef = MPP_CONSTRAINT_TYPE[textOf(t, 'ConstraintType') ?? ''];
    let constraint: ImportConstraint | undefined;
    let extraFields: Record<string, unknown> | undefined;
    if (constraintDef) {
      const rawDate = textOf(t, 'ConstraintDate');
      const date = rawDate ? rawDate.slice(0, 19) : null;   // strip sub-second noise
      constraint = { typeValue: constraintDef.value, typeLabel: constraintDef.label, date };
      extraFields = { dagrun_pm_constrainttype: constraintDef.value };
      if (date) extraFields.dagrun_pm_constraintdate = date;
      constraintCount++;
    }

    rows.push([
      name,
      start ? start.slice(0, 10) : null,
      finish ? finish.slice(0, 10) : null,
      pct != null ? Number(pct) : null,
      msDurationToHours(work),
      resources || null,
      wbs || null,
    ]);
    taskMeta.push({ uid, collectionIndex: currentCollectionIndex, predecessors, extraFields, constraint });
  });

  // No OutlineLevel 1 phases (flat project) → one default collection.
  if (collections.length === 0) {
    collections.push('Imported Tasks');
    taskMeta.forEach(m => { m.collectionIndex = 0; });
  } else if (taskMeta.some(m => m.collectionIndex < 0)) {
    // Orphan work items that precede any phase → a default collection.
    const orphanIndex = collections.length;
    collections.push('Imported Tasks');
    taskMeta.forEach(m => { if (m.collectionIndex < 0) m.collectionIndex = orphanIndex; });
  }

  return { headers, rows, collections, taskMeta, dependencyCount, constraintCount };
}

// ── Monday.com ───────────────────────────────────────────────────────

const MONDAY_ENDPOINT = 'https://api.monday.com/v2';

async function mondayGraphql<T>(token: string, query: string): Promise<T> {
  const res = await proxyFetch(MONDAY_ENDPOINT, {
    method: 'POST',
    headers: {
      'Authorization': token,
      'Content-Type': 'application/json',
      'API-Version': '2024-01',
    },
    body: JSON.stringify({ query }),
  });
  if (!res.ok) {
    throw new Error(`Monday.com API ${res.status}: ${res.statusText}`);
  }
  const json = await res.json();
  if (json.errors && json.errors.length > 0) {
    throw new Error(`Monday.com error: ${json.errors[0].message}`);
  }
  return json.data as T;
}

export async function fetchMondayBoards(token: string): Promise<{ id: string; name: string }[]> {
  const data = await mondayGraphql<{ boards: { id: string; name: string }[] }>(
    token,
    '{ boards(limit: 50) { id name } }',
  );
  return data.boards ?? [];
}

interface MondayItem {
  id: string;
  name: string;
  column_values: { id: string; text: string | null; value: string | null }[];
}
interface MondayColumn { id: string; title: string; type: string }

interface MondayBoardResponse {
  boards: { columns: MondayColumn[]; items_page: { cursor: string | null; items: MondayItem[] } }[];
}

export async function fetchMondayItems(token: string, boardId: string): Promise<{ headers: string[]; rows: unknown[][] }> {
  // Monday v2 API paginates items_page; single page with 200 items is the
  // default ceiling per request. For larger boards we iterate with cursor.
  const allItems: MondayItem[] = [];
  let columns: MondayColumn[] = [];
  let cursor: string | null = null;

  for (;;) {
    const cursorClause: string = cursor ? `, cursor: "${cursor}"` : '';
    const query: string = `{
      boards(ids: [${boardId}]) {
        columns { id title type }
        items_page(limit: 200${cursorClause}) {
          cursor
          items { id name column_values { id text value } }
        }
      }
    }`;
    const data: MondayBoardResponse = await mondayGraphql<MondayBoardResponse>(token, query);
    const b = data.boards?.[0];
    if (!b) return { headers: [], rows: [] };
    columns = b.columns ?? [];
    allItems.push(...(b.items_page.items ?? []));
    cursor = b.items_page.cursor;
    if (!cursor) break;
    if (allItems.length >= 2000) break; // safety cap
  }

  // Build headers from column titles (prepend "Name" for item name)
  const headers = ['Name', ...columns.map(c => c.title)];

  // Build rows with the item name first, then each column's text value.
  const rows: unknown[][] = allItems.map(item => {
    const byId = new Map(item.column_values.map(cv => [cv.id, cv]));
    const row: unknown[] = [item.name];
    columns.forEach(col => {
      const cv = byId.get(col.id);
      if (!cv) { row.push(null); return; }
      // For "person" columns, try to extract display name from JSON value.
      if (col.type === 'person' && cv.value) {
        try {
          const v = JSON.parse(cv.value);
          if (v.personsAndTeams?.length) {
            // We don't have the display names in the query result without
            // another hop — fall back to cv.text which Monday populates
            // with the comma-separated display names.
          }
        } catch { /* ignore */ }
      }
      row.push(cv.text ?? null);
    });
    return row;
  });

  return { headers, rows };
}

// ── Smartsheet ───────────────────────────────────────────────────────

const SMARTSHEET_ENDPOINT = 'https://api.smartsheet.com/2.0';

async function smartsheetGet<T>(token: string, path: string): Promise<T> {
  const res = await proxyFetch(`${SMARTSHEET_ENDPOINT}${path}`, {
    headers: { 'Authorization': `Bearer ${token}` },
  });
  if (res.status === 401) throw new Error('Invalid token — check your Smartsheet access token.');
  if (!res.ok) throw new Error(`Smartsheet API ${res.status}: ${res.statusText}`);
  return res.json() as Promise<T>;
}

export async function fetchSmartsheetSheets(token: string): Promise<{ id: string; name: string }[]> {
  const data = await smartsheetGet<{ data: { id: number; name: string }[] }>(token, '/sheets?includeAll=true');
  return (data.data ?? []).map(s => ({ id: String(s.id), name: s.name }));
}

interface SmartsheetColumn { id: number; title: string; type: string }
interface SmartsheetCell { columnId: number; value: unknown; displayValue?: string }
interface SmartsheetRow { id: number; cells: SmartsheetCell[] }

export async function fetchSmartsheetSheet(token: string, sheetId: string): Promise<{ headers: string[]; rows: unknown[][] }> {
  const data = await smartsheetGet<{ columns: SmartsheetColumn[]; rows: SmartsheetRow[] }>(token, `/sheets/${sheetId}`);
  const headers = (data.columns ?? []).map(c => c.title);
  const rows: unknown[][] = (data.rows ?? []).map(r => {
    const cellByCol = new Map<number, SmartsheetCell>();
    r.cells.forEach(c => cellByCol.set(c.columnId, c));
    return (data.columns ?? []).map(col => {
      const cell = cellByCol.get(col.id);
      if (!cell) return null;
      return cell.displayValue ?? cell.value ?? null;
    });
  });
  return { headers, rows };
}

// ── Jira Cloud ───────────────────────────────────────────────────────

interface JiraCreds { email: string; token: string; domain: string }

async function jiraGet<T>(creds: JiraCreds, path: string): Promise<T> {
  const auth = btoa(`${creds.email}:${creds.token}`);
  const url = `https://${creds.domain}${path}`;
  const res = await proxyFetch(url, {
    headers: {
      'Authorization': `Basic ${auth}`,
      'Accept': 'application/json',
    },
  });
  if (res.status === 401) throw new Error('Jira authentication failed — check your email + API token.');
  if (!res.ok) throw new Error(`Jira API ${res.status}: ${res.statusText}`);
  return res.json() as Promise<T>;
}

export async function fetchJiraProjects(creds: JiraCreds): Promise<{ id: string; key: string; name: string }[]> {
  const data = await jiraGet<{ id: string; key: string; name: string }[]>(creds, '/rest/api/3/project');
  return (data ?? []).map(p => ({ id: p.id, key: p.key, name: p.name }));
}

// Atlassian Document Format → plain text walker. Recursively concatenates
// any `text` node content. Good enough for a description preview.
function adfToText(doc: unknown): string {
  if (!doc || typeof doc !== 'object') return '';
  const d = doc as { type?: string; text?: string; content?: unknown[] };
  if (d.type === 'text' && typeof d.text === 'string') return d.text;
  if (Array.isArray(d.content)) return d.content.map(adfToText).join('');
  return '';
}

interface JiraIssue {
  key: string;
  fields: {
    summary?: string;
    status?: { name?: string };
    assignee?: { displayName?: string };
    priority?: { name?: string };
    duedate?: string | null;
    description?: unknown;
  };
}

export async function fetchJiraIssues(creds: JiraCreds, projectKey: string): Promise<{ headers: string[]; rows: unknown[][] }> {
  const all: JiraIssue[] = [];
  let startAt = 0;
  const pageSize = 100;
  for (;;) {
    const path = `/rest/api/3/search?jql=${encodeURIComponent(`project=${projectKey}`)}&fields=summary,status,assignee,priority,duedate,description&maxResults=${pageSize}&startAt=${startAt}`;
    const page = await jiraGet<{ issues: JiraIssue[]; total: number }>(creds, path);
    all.push(...(page.issues ?? []));
    startAt += page.issues?.length ?? 0;
    if (!page.issues || page.issues.length < pageSize) break;
    if (all.length >= 2000) break; // safety
  }
  const headers = ['Key', 'Summary', 'Status', 'Assignee', 'Priority', 'Due Date', 'Description'];
  const rows: unknown[][] = all.map(i => [
    i.key,
    i.fields.summary ?? null,
    i.fields.status?.name ?? null,
    i.fields.assignee?.displayName ?? null,
    i.fields.priority?.name ?? null,
    i.fields.duedate ?? null,
    adfToText(i.fields.description) || null,
  ]);
  return { headers, rows };
}

// ── Asana ─────────────────────────────────────────────────────────

async function asanaGet<T>(token: string, path: string): Promise<T> {
  const res = await proxyFetch(`https://app.asana.com/api/1.0${path}`, {
    headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' },
  });
  if (res.status === 401) throw new Error('Asana authentication failed — check your Personal Access Token.');
  if (!res.ok) throw new Error(`Asana API ${res.status}: ${res.statusText}`);
  return res.json() as Promise<T>;
}

export async function fetchAsanaWorkspaces(token: string): Promise<{ gid: string; name: string }[]> {
  const data = await asanaGet<{ data: { gid: string; name: string }[] }>(token, '/workspaces');
  return data.data ?? [];
}

export async function fetchAsanaProjects(token: string, workspaceGid: string): Promise<{ gid: string; name: string }[]> {
  const data = await asanaGet<{ data: { gid: string; name: string }[] }>(token, `/projects?workspace=${workspaceGid}&limit=100`);
  return data.data ?? [];
}

interface AsanaTask {
  gid: string;
  name: string;
  completed?: boolean;
  due_on?: string | null;
  assignee?: { name?: string } | null;
  notes?: string;
}

export async function fetchAsanaTasks(token: string, projectGid: string): Promise<{ headers: string[]; rows: unknown[][] }> {
  const data = await asanaGet<{ data: AsanaTask[] }>(token,
    `/tasks?project=${projectGid}&opt_fields=name,completed,due_on,assignee.name,notes&limit=100`);
  const tasks = data.data ?? [];
  const headers = ['Name', 'Status', 'Due', 'Assignee', 'Notes'];
  const rows: unknown[][] = tasks.map(t => [
    t.name,
    t.completed ? 'Done' : 'In Progress',
    t.due_on ?? null,
    t.assignee?.name ?? null,
    t.notes ?? null,
  ]);
  return { headers, rows };
}

// ── ClickUp ───────────────────────────────────────────────────────

async function clickupGet<T>(token: string, path: string): Promise<T> {
  const res = await proxyFetch(`https://api.clickup.com/api/v2${path}`, {
    headers: { 'Authorization': token, 'Accept': 'application/json' },
  });
  if (res.status === 401) throw new Error('ClickUp authentication failed — check your API token.');
  if (!res.ok) throw new Error(`ClickUp API ${res.status}: ${res.statusText}`);
  return res.json() as Promise<T>;
}

export async function fetchClickUpTeams(token: string): Promise<{ id: string; name: string }[]> {
  const data = await clickupGet<{ teams: { id: string; name: string }[] }>(token, '/team');
  return data.teams ?? [];
}

export async function fetchClickUpSpaces(token: string, teamId: string): Promise<{ id: string; name: string }[]> {
  const data = await clickupGet<{ spaces: { id: string; name: string }[] }>(token, `/team/${teamId}/space?archived=false`);
  return data.spaces ?? [];
}

export async function fetchClickUpLists(token: string, spaceId: string): Promise<{ id: string; name: string }[]> {
  // Lists can be nested inside folders in ClickUp. Fetch folderless lists
  // first; then iterate folders and collect their lists.
  const folderless = await clickupGet<{ lists: { id: string; name: string }[] }>(token, `/space/${spaceId}/list?archived=false`);
  const lists = [...(folderless.lists ?? [])];
  try {
    const folders = await clickupGet<{ folders: { id: string; lists: { id: string; name: string }[] }[] }>(token, `/space/${spaceId}/folder?archived=false`);
    (folders.folders ?? []).forEach(f => { lists.push(...(f.lists ?? [])); });
  } catch { /* non-fatal */ }
  return lists;
}

interface ClickUpTask {
  name: string;
  status?: { status?: string };
  assignees?: { username?: string }[];
  due_date?: string | null; // ms timestamp as string
  description?: string;
  priority?: { priority?: string } | null;
}

export async function fetchClickUpTasks(token: string, listId: string): Promise<{ headers: string[]; rows: unknown[][] }> {
  const all: ClickUpTask[] = [];
  let page = 0;
  for (;;) {
    const data = await clickupGet<{ tasks: ClickUpTask[] }>(token, `/list/${listId}/task?include_closed=true&page=${page}`);
    all.push(...(data.tasks ?? []));
    if (!data.tasks || data.tasks.length < 100) break;
    page++;
    if (all.length >= 2000) break;
  }
  const headers = ['Name', 'Status', 'Assignee', 'Due Date', 'Priority', 'Description'];
  const rows: unknown[][] = all.map(t => {
    const dueMs = t.due_date ? parseInt(t.due_date, 10) : null;
    const dueIso = dueMs && !isNaN(dueMs) ? new Date(dueMs).toISOString().slice(0, 10) : null;
    return [
      t.name,
      t.status?.status ?? null,
      t.assignees?.[0]?.username ?? null,
      dueIso,
      t.priority?.priority ?? null,
      t.description ?? null,
    ];
  });
  return { headers, rows };
}

// ── Trello ────────────────────────────────────────────────────────

interface TrelloCreds { key: string; token: string }

async function trelloGet<T>(creds: TrelloCreds, path: string): Promise<T> {
  const sep = path.includes('?') ? '&' : '?';
  const url = `https://api.trello.com/1${path}${sep}key=${encodeURIComponent(creds.key)}&token=${encodeURIComponent(creds.token)}`;
  const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
  if (res.status === 401) throw new Error('Trello authentication failed — check your API key + token.');
  if (!res.ok) throw new Error(`Trello API ${res.status}: ${res.statusText}`);
  return res.json() as Promise<T>;
}

export async function fetchTrelloBoards(creds: TrelloCreds): Promise<{ id: string; name: string }[]> {
  const data = await trelloGet<{ id: string; name: string }[]>(creds, '/members/me/boards?fields=name');
  return data ?? [];
}

interface TrelloList { id: string; name: string }
interface TrelloCard {
  id: string;
  name: string;
  desc?: string;
  due?: string | null;
  idList?: string;
  idMembers?: string[];
}

export async function fetchTrelloCards(creds: TrelloCreds, boardId: string): Promise<{ headers: string[]; rows: unknown[][] }> {
  const [lists, cards] = await Promise.all([
    trelloGet<TrelloList[]>(creds, `/boards/${boardId}/lists?fields=name`),
    trelloGet<TrelloCard[]>(creds, `/boards/${boardId}/cards?fields=name,desc,due,idMembers,idList`),
  ]);
  const listById = new Map(lists.map(l => [l.id, l.name]));

  // Resolve member display names. One request per unique member id.
  const allMemberIds = new Set<string>();
  cards.forEach(c => (c.idMembers ?? []).forEach(id => allMemberIds.add(id)));
  const memberNames = new Map<string, string>();
  for (const id of Array.from(allMemberIds)) {
    try {
      const m = await trelloGet<{ id: string; fullName?: string }>(creds, `/members/${id}?fields=fullName`);
      if (m.fullName) memberNames.set(id, m.fullName);
    } catch { /* skip */ }
    if (memberNames.size >= 200) break; // safety cap
  }

  const headers = ['Name', 'Status', 'Due', 'Assignee', 'Description'];
  const rows: unknown[][] = cards.map(c => {
    const status = c.idList ? (listById.get(c.idList) ?? null) : null;
    const assignee = (c.idMembers ?? []).map(id => memberNames.get(id)).filter(Boolean).join(', ') || null;
    return [
      c.name,
      status,
      c.due ? c.due.slice(0, 10) : null,
      assignee,
      c.desc ?? null,
    ];
  });
  return { headers, rows };
}

// ── Project Online (PWA ProjectData OData) ─────────────────────────────
//
// Calls go through the import proxy (the allowlist permits *.sharepoint.com
// PWA OData paths). The user's existing M365 login is used — a SharePoint-
// scoped token for the PWA host is acquired silently via MSAL.

function poDate(s: unknown): string | null {
  if (!s || typeof s !== 'string') return null;
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const epoch = s.match(/\/Date\((\d+)/);            // OData v3 "/Date(ms)/"
  if (epoch) { const d = new Date(Number(epoch[1])); return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10); }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d.toISOString().slice(0, 10);
}

const PO_DEP_TYPE: Record<string, number> = { FS: 100000000, SS: 100000001, FF: 100000002, SF: 100000003 };

// TaskPredecessors is e.g. "3FS;7SS" — number is the predecessor task's
// 1-indexed position in the result set, optionally followed by a link type.
function parsePoPredecessors(value: unknown): ImportPredecessor[] {
  if (!value || typeof value !== 'string') return [];
  const out: ImportPredecessor[] = [];
  value.split(/[;,]/).forEach(part => {
    const m = part.trim().match(/^(\d+)\s*(FS|SS|FF|SF)?/i);
    if (!m) return;
    out.push({ uid: m[1], type: PO_DEP_TYPE[(m[2] ?? 'FS').toUpperCase()] ?? PO_DEP_TYPE.FS });
  });
  return out;
}

async function projectOnlineGet(pca: IPublicClientApplication, pwaUrl: string, odataPath: string): Promise<any> {
  const host = new URL(pwaUrl).host;
  const token = await getToken(pca, [`https://${host}/.default`]);
  const res = await proxyFetch(`${pwaUrl.replace(/\/$/, '')}${odataPath}`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json;odata=nometadata' },
  });
  const ct = (res.headers.get('content-type') ?? '').toLowerCase();
  if (ct.startsWith('text/html')) {
    throw new Error('Authentication redirect detected. Your session may have expired — please reload and try again.');
  }
  if (res.status === 401) throw new Error("Could not authenticate. Make sure you're signed into the correct Microsoft 365 account that has access to this Project Online site.");
  if (res.status === 403) throw new Error('Access denied. You need at least Read access to the PWA site.');
  if (res.status === 404) throw new Error('Project Online site not found. Check your URL and try again.');
  if (!res.ok) throw new Error(`Project Online request failed: ${res.status}`);
  return res.json();
}

export async function fetchProjectOnlineProjects(
  pca: IPublicClientApplication, pwaUrl: string,
): Promise<{ id: string; name: string; start: string | null; finish: string | null }[]> {
  const json = await projectOnlineGet(pca, pwaUrl, '/_api/ProjectData/Projects?$select=ProjectId,ProjectName,ProjectStartDate,ProjectFinishDate');
  const arr: any[] = json.value ?? json.d?.results ?? [];
  return arr.map(p => ({
    id: String(p.ProjectId),
    name: p.ProjectName ?? '(unnamed project)',
    start: poDate(p.ProjectStartDate),
    finish: poDate(p.ProjectFinishDate),
  }));
}

export async function fetchProjectOnlineTasks(
  pca: IPublicClientApplication, pwaUrl: string, projectId: string,
): Promise<ParsedProjectXml> {
  const select = 'TaskId,TaskName,TaskStartDate,TaskFinishDate,TaskPercentCompleted,TaskOutlineLevel,TaskPredecessors,TaskResourceNames,TaskBaselineStartDate,TaskBaselineFinishDate,TaskActualStartDate,TaskActualFinishDate';
  const json = await projectOnlineGet(pca, pwaUrl, `/_api/ProjectData/Projects(guid'${projectId}')/Tasks?$select=${select}`);
  const tasks: any[] = json.value ?? json.d?.results ?? [];

  const headers = ['Task Name', 'Start', 'Finish', '% Complete', 'Resources'];
  const collections: string[] = [];
  const taskMeta: ImportTaskMeta[] = [];
  const rows: unknown[][] = [];
  let currentCollectionIndex = -1;
  let dependencyCount = 0;

  tasks.forEach((t, idx) => {
    const position = idx + 1;              // 1-indexed — what TaskPredecessors references
    const name = t.TaskName;
    if (!name) return;
    const level = Number(t.TaskOutlineLevel);
    if (level === 0) return;               // project summary
    if (level === 1) { collections.push(name); currentCollectionIndex = collections.length - 1; return; }

    const preds = parsePoPredecessors(t.TaskPredecessors);
    dependencyCount += preds.length;

    rows.push([
      name,
      poDate(t.TaskStartDate),
      poDate(t.TaskFinishDate),
      t.TaskPercentCompleted != null ? Number(t.TaskPercentCompleted) : null,
      t.TaskResourceNames || null,
    ]);

    const extra: Record<string, unknown> = {};
    const bStart = poDate(t.TaskBaselineStartDate);
    const bEnd = poDate(t.TaskBaselineFinishDate);
    if (bStart) extra.dagrun_pm_baselinestart = bStart;
    if (bEnd) extra.dagrun_pm_baselineend = bEnd;

    taskMeta.push({
      uid: String(position),
      collectionIndex: currentCollectionIndex,
      predecessors: preds,
      extraFields: Object.keys(extra).length ? extra : undefined,
    });
  });

  if (collections.length === 0) {
    collections.push('Imported Tasks');
    taskMeta.forEach(m => { m.collectionIndex = 0; });
  } else if (taskMeta.some(m => m.collectionIndex < 0)) {
    const orphanIndex = collections.length;
    collections.push('Imported Tasks');
    taskMeta.forEach(m => { if (m.collectionIndex < 0) m.collectionIndex = orphanIndex; });
  }

  // Constraint import is MPP-XML-only for now (ProjectData's
  // TaskConstraintType is not queried here).
  return { headers, rows, collections, taskMeta, dependencyCount, constraintCount: 0 };
}
